import { useCallback, useEffect, useRef, useState, type DragEvent } from 'react'
import { Screen } from '@/components/layout'
import { Button } from '@/components/primitives'
import { ProgressBar } from '@/components/charts/Bars'
import { IconFile, IconUpload } from '@/components/icons'
import type { Statement } from '@/generated/types'
import { ApiFailure, api } from '@/lib/api'
import { t, tServer, type CopyKey } from '@/lib/copy'
import { formatFileSize } from '@/lib/format'
import { useNavigate } from '@/lib/router'
import { bridge } from '@/lib/bridge'

const MAX_BYTES = 25 * 1024 * 1024
const ACCEPT = '.pdf,.csv,application/pdf,text/csv'

type FileStatus = 'pending' | 'uploading' | 'parsing' | 'ready' | 'failed'

interface QueuedFile {
  key: string
  file: File
  status: FileStatus
  statementId?: string
  errorKey?: CopyKey
  errorText?: string
}

const FAILURE_COPY: Record<string, CopyKey> = {
  passwordProtected: 'importReview.locked.title',
  unreadable: 'upload.error.unreadable',
  unsupportedFormat: 'upload.error.unsupported',
  noTransactionsFound: 'upload.error.noTransactions',
  duplicate: 'upload.error.duplicate',
  tooLarge: 'upload.error.tooLarge',
}

const STATUS_COPY: Record<FileStatus, CopyKey> = {
  pending: 'upload.file.pending',
  uploading: 'upload.file.uploading',
  parsing: 'upload.file.parsing',
  ready: 'upload.file.done',
  failed: 'upload.file.failed',
}

/**
 * Drag-and-drop and file picker, several files at once, per-file status.
 * A failure on one file never blocks the others — each row polls its own
 * statement and reports its own outcome.
 */
export function UploadScreen() {
  const navigate = useNavigate()
  const inputRef = useRef<HTMLInputElement>(null)
  const [dragging, setDragging] = useState(false)
  const [queue, setQueue] = useState<QueuedFile[]>([])

  const update = useCallback((key: string, patch: Partial<QueuedFile>) => {
    setQueue((rows) => rows.map((row) => (row.key === key ? { ...row, ...patch } : row)))
  }, [])

  const accept = useCallback((files: FileList | File[]) => {
    const next: QueuedFile[] = []
    for (const file of Array.from(files)) {
      const key = `${file.name}:${file.size}:${file.lastModified}`
      const extension = file.name.toLowerCase().split('.').pop()
      if (extension !== 'pdf' && extension !== 'csv') {
        next.push({ key, file, status: 'failed', errorKey: 'upload.error.unsupported' })
      } else if (file.size > MAX_BYTES) {
        next.push({ key, file, status: 'failed', errorKey: 'upload.error.tooLarge' })
      } else {
        next.push({ key, file, status: 'pending' })
      }
    }
    setQueue((rows) => [...rows, ...next.filter((row) => !rows.some((r) => r.key === row.key))])
  }, [])

  const onDrop = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault()
    setDragging(false)
    if (event.dataTransfer.files.length) accept(event.dataTransfer.files)
  }

  const uploadAll = async () => {
    const pending = queue.filter((row) => row.status === 'pending')
    await Promise.all(
      pending.map(async (row) => {
        update(row.key, { status: 'uploading' })
        try {
          const statement = await api.statements.upload(row.file, { idempotencyKey: crypto.randomUUID() })
          update(row.key, { status: 'parsing', statementId: statement.id })
        } catch (e) {
          const failure = e instanceof ApiFailure ? e : null
          bridge.reportError(`upload failed for ${row.file.name}`, 'upload')
          update(row.key, {
            status: 'failed',
            errorText: failure ? tServer(failure.copyKey, undefined, 'upload.error.failed') : t('upload.error.failed'),
          })
        }
      }),
    )
  }

  const onParsed = useCallback(
    (key: string, statement: Statement) => {
      if (statement.status === 'needsReview' || statement.status === 'imported') {
        update(key, { status: 'ready', statementId: statement.id })
        return
      }
      if (statement.status === 'failed') {
        update(key, {
          status: 'failed',
          errorKey: FAILURE_COPY[statement.failureReason ?? ''] ?? 'upload.error.failed',
          statementId: statement.id,
        })
      }
    },
    [update],
  )

  const parsing = queue.filter((row) => row.status === 'parsing')
  const uploading = queue.some((row) => row.status === 'uploading')
  const unsupported = queue.some((row) => row.errorKey === 'upload.error.unsupported' || row.errorKey === 'upload.error.tooLarge')
  const failed = queue.some((row) => row.status === 'failed' && !row.errorKey)
  const ready = queue.filter((row) => row.status === 'ready')
  const pendingCount = queue.filter((row) => row.status === 'pending').length

  const screenState = uploading
    ? 'uploading'
    : parsing.length > 0
      ? 'parsing'
      : unsupported
        ? 'unsupported'
        : failed
          ? 'error'
          : 'ready'

  // One file, parsed cleanly, nothing else in flight -> straight to review.
  useEffect(() => {
    if (queue.length === 1 && ready.length === 1 && ready[0].statementId) {
      navigate('importReview', { statementId: ready[0].statementId })
    }
  }, [queue.length, ready, navigate])

  const done = queue.length - pendingCount - parsing.length - (uploading ? 1 : 0)

  return (
    <Screen
      id="upload"
      state={screenState}
      actions={
        queue.length > 0 ? (
          <Button variant="secondary" onClick={() => inputRef.current?.click()}>
            {t('upload.cta.addMore')}
          </Button>
        ) : undefined
      }
    >
      <div className="stack">
        <div
          className="dropzone"
          data-active={dragging}
          onDragOver={(e) => {
            e.preventDefault()
            setDragging(true)
          }}
          onDragLeave={() => setDragging(false)}
          onDrop={onDrop}
          role="button"
          tabIndex={0}
          aria-label={t('a11y.uploadDropZone')}
          onClick={() => inputRef.current?.click()}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') inputRef.current?.click()
          }}
        >
          <IconUpload size={32} />
          <span className="dropzone-title">{t('upload.dropZone')}</span>
          <Button variant="secondary" onClick={() => inputRef.current?.click()}>
            {t('upload.cta.choose')}
          </Button>
          <span className="caption">{t('upload.helper')}</span>
        </div>

        {/* The other ingestion path. Same pipeline, same rows — offered here so
            upload is a choice rather than the only way in. */}
        <div className="row">
          <Button variant="quiet" onClick={() => navigate('linkAccount')}>
            {t('upload.cta.link')}
          </Button>
        </div>

        <input
          ref={inputRef}
          type="file"
          className="visually-hidden"
          accept={ACCEPT}
          multiple
          onChange={(e) => {
            if (e.target.files) accept(e.target.files)
            e.target.value = ''
          }}
        />

        {queue.length > 0 ? (
          <div className="file-rows">
            {queue.map((row) => (
              <FileRow
                key={row.key}
                row={row}
                onParsed={onParsed}
                onRemove={() => setQueue((rows) => rows.filter((r) => r.key !== row.key))}
                onContinue={() =>
                  row.statementId ? navigate('importReview', { statementId: row.statementId }) : undefined
                }
              />
            ))}
          </div>
        ) : null}

        {queue.length > 1 ? (
          <div className="stack-sm">
            <ProgressBar ratio={queue.length ? done / queue.length : 0} label={t('upload.progress', { done, total: queue.length })} />
            <span className="caption">{t('upload.progress', { done, total: queue.length })}</span>
          </div>
        ) : null}

        {pendingCount > 0 ? (
          <Button variant="primary" onClick={() => void uploadAll()} disabled={uploading}>
            {t('upload.cta.upload')}
          </Button>
        ) : null}
      </div>
    </Screen>
  )
}

/** Polls its own statement: every 1s, backing off to 3s after 15s. */
function FileRow({
  row,
  onParsed,
  onRemove,
  onContinue,
}: {
  row: QueuedFile
  onParsed: (key: string, statement: Statement) => void
  onRemove: () => void
  onContinue: () => void
}) {
  useEffect(() => {
    if (row.status !== 'parsing' || !row.statementId) return
    const startedAt = Date.now()
    let cancelled = false
    let timer: number

    const poll = async () => {
      try {
        const statement = await api.statements.get(row.statementId!)
        if (cancelled) return
        if (statement.status === 'queued' || statement.status === 'parsing') {
          timer = window.setTimeout(poll, Date.now() - startedAt > 15_000 ? 3000 : 1000)
        } else {
          onParsed(row.key, statement)
        }
      } catch {
        if (!cancelled) onParsed(row.key, { ...({} as Statement), status: 'failed', failureReason: 'unreadable' })
      }
    }

    timer = window.setTimeout(poll, 400)
    return () => {
      cancelled = true
      clearTimeout(timer)
    }
  }, [row.status, row.statementId, row.key, onParsed])

  const detail = row.errorText ?? (row.errorKey ? t(row.errorKey, { limit: formatFileSize(MAX_BYTES) }) : null)

  return (
    <div className="card row" style={{ gap: 'var(--space-md)' }}>
      <span className="avatar" aria-hidden="true">
        <IconFile size={18} />
      </span>
      <span className="list-row-main">
        <span className="list-row-title">{row.file.name}</span>
        <span className="list-row-sub">
          <span>{formatFileSize(row.file.size)}</span>
          <span className={row.status === 'failed' ? 'negative' : undefined}>{t(STATUS_COPY[row.status])}</span>
        </span>
        {detail ? <span className="inline-error">{detail}</span> : null}
      </span>
      {row.status === 'ready' ? (
        <Button variant="primary" onClick={onContinue}>
          {t('cta.continue')}
        </Button>
      ) : null}
      <Button variant="quiet" onClick={onRemove} aria-label={t('upload.file.remove')}>
        {t('upload.file.remove')}
      </Button>
    </div>
  )
}
